/**
 * API Service for backend communication
 */

import { MonthlyCapacity } from "../config/availableEffort";
import { ClientProject } from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "/api";

export interface ScheduleMetadata {
  id: string;
  filename: string;
  savedAt: string;
  projectCount: number;
  categoryCount: number;
}

export interface SaveScheduleResponse {
  success: boolean;
  message: string;
  id: string;
  savedAt: string;
}

export interface ScheduleData {
  categories: any[];
  capacity?: MonthlyCapacity;
  clientProjects?: ClientProject[];
  metadata?: {
    savedAt: string;
    version: string;
  };
}

/**
 * Fetch all saved schedules
 */
export async function listSchedules(): Promise<ScheduleMetadata[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules`);

    if (!response.ok) {
      throw new Error(`Failed to list schedules: ${response.statusText}`);
    }

    const data = await response.json();
    return data.schedules || [];
  } catch (error) {
    console.error("Error listing schedules:", error);
    throw error;
  }
}

/**
 * Load a specific schedule by ID
 */
export async function loadSchedule(id: string): Promise<ScheduleData> {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Schedule not found");
      }
      throw new Error(`Failed to load schedule: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error loading schedule:", error);
    throw error;
  }
}

/**
 * Save a schedule to the backend
 */
export async function saveSchedule(
  id: string,
  categories: any[],
  capacity?: MonthlyCapacity,
  clientProjects?: ClientProject[],
): Promise<SaveScheduleResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, categories, capacity, clientProjects }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to save schedule: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving schedule:", error);
    throw error;
  }
}

/**
 * Delete a schedule from the backend
 */
export async function deleteSchedule(id: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Schedule not found");
      }
      throw new Error(`Failed to delete schedule: ${response.statusText}`);
    }
  } catch (error) {
    console.error("Error deleting schedule:", error);
    throw error;
  }
}

/**
 * Check backend health
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Serialize categories for saving (convert to plain objects)
 */
export function serializeCategories(categories: any[]): any[] {
  return categories.map((category) => ({
    id: category.id,
    name: category.name,
    color: category.color,
    parentId: category.parentId,
    level: category.level,
    projects: category.getProjects().map((project: any) => ({
      id: project.id,
      name: project.name,
      startDate: project.startDate,
      endDate: project.endDate,
      color: project.color,
      status: project.status,
      effortBreakdown: project.effortBreakdown,
      tasks: project.tasks || [],
      clientProjectIds: project.clientProjectIds || [],
    })),
    subcategories: serializeCategories(category.getSubcategories()),
  }));
}

/**
 * Deserialize categories from backend (reconstruct Category/Project objects)
 */
export function deserializeCategories(data: any[]): any[] {
  // Import will be done in the component where Category and Project classes are available
  // This function will be called from the component with proper imports
  return data;
}
